import { Events } from "app-interfaces";
import { DateTime } from "luxon";
import { v4 as uuid } from "uuid";

import ServerApi from "../../../../server-api";
import { EventType } from "../../../../types/StaticTypes";
import { toDateTimeWithZ } from "../../../../utils/time-utils";

export type DefaultAvailabilityEvent = Events.ResponseServerEvent<
  Events.SessionFull,
  Events.BaseEventAvailability
>;
export type DefaultAvailabilityWeekDayName = DefaultAvailabilityEvent["name"];

export type DefaultAvailabilityEventListDTO = { events: DefaultAvailabilityEvent[] };

export type SaveDefaultAvailabilityResponse = { id: string };

/**
 * Get the current default availability settings (events) for each day of the week.
 *
 * The "from" and "to" dates are set to now, because we want the current settings.
 * The API will return the latest availability event for each day of the week,
 * even if it was created before the "from" date. See API docs for more info.
 */
export function getCurrentDefaultAvailabilitySettings(
  api: ServerApi
): Promise<DefaultAvailabilityEventListDTO> {
  const now = toDateTimeWithZ(DateTime.local());

  return api.getEvents({
    from: now,
    to: now,
    types: [EventType.Availability],
  });
}

/**
 * Save default availability for some day of the week.
 *
 * The API doesn't allow to update availability in order to keep historical data.
 * So we have to POST a new event, with new uuid, and "created" set to now.
 */
export function saveDefaultAvailability(
  api: ServerApi,
  defaultAvailabilityEvent: DefaultAvailabilityEvent
): Promise<SaveDefaultAvailabilityResponse> {
  const newDefaultAvailabilityEvent = {
    ...defaultAvailabilityEvent,
    id: uuid(),
    created: api.serializeDate(new Date()),
  };

  return api.postEvents(newDefaultAvailabilityEvent);
}
